import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Plus, CheckSquare, MoreHorizontal, Pencil, Trash2, Ban, Clock } from 'lucide-react';
import { Header, PageContainer } from '@/components/layout';
import {
  Button,
  Card,
  CardBody,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  PriorityBadge,
  Badge,
  Avatar,
  EmptyState,
  Input,
  Select,
  Dropdown,
  DropdownItem,
  ConfirmDialog,
} from '@/components/ui';
import { TaskFormModal } from '@/components/forms';
import { useTasks, useUpdateTask, useDeleteTask } from '@/hooks/useTasks';
import { useProjects } from '@/hooks/useProjects';
import { useAuthStore } from '@/stores/auth';
import { formatDate, formatDuration } from '@aelita/shared';
import type { Task } from '@aelita/shared';

export const Route = createFileRoute('/tasks')({
  component: TasksPage,
});

function TasksPage() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [deletingTask, setDeletingTask] = useState<Task | null>(null);
  const [filters, setFilters] = useState<{
    status?: string;
    priority?: string;
    projectId?: string;
    search?: string;
  }>({});
  const { data, isLoading } = useTasks(filters);
  const { data: projectsData } = useProjects({ status: 'active' });
  const deleteTask = useDeleteTask();
  const tasks = data?.data ?? [];
  const projects = projectsData?.data ?? [];
  const user = useAuthStore((s) => s.user);

  // Role checks - managers+ can delete tasks
  const isManager = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'pm';

  const handleDelete = async () => {
    if (!deletingTask) return;
    try {
      await deleteTask.mutateAsync(deletingTask.id);
      setDeletingTask(null);
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  return (
    <>
      <Header
        title="Tasks"
        subtitle="Manage all your tasks across projects"
        actions={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setIsCreateOpen(true)}>
            New Task
          </Button>
        }
      />
      <PageContainer>
        {/* Filters */}
        <Card className="mb-6">
          <CardBody className="flex items-center gap-4 flex-wrap">
            <Input
              placeholder="Search tasks..."
              className="max-w-xs"
              value={filters.search ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            />
            <Select
              options={[
                { value: '', label: 'All Projects' },
                ...projects.map((p: any) => ({ value: p.id, label: p.name })),
              ]}
              value={filters.projectId ?? ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, projectId: e.target.value || undefined }))
              }
              className="w-48"
            />
            <Select
              options={[
                { value: '', label: 'All Statuses' },
                { value: 'backlog', label: 'Backlog' },
                { value: 'todo', label: 'To Do' },
                { value: 'in_progress', label: 'In Progress' },
                { value: 'review', label: 'In Review' },
                { value: 'done', label: 'Done' },
              ]}
              value={filters.status ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value || undefined }))}
              className="w-36"
            />
            <Select
              options={[
                { value: '', label: 'All Priorities' },
                { value: 'critical', label: 'Critical' },
                { value: 'high', label: 'High' },
                { value: 'medium', label: 'Medium' },
                { value: 'low', label: 'Low' },
              ]}
              value={filters.priority ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value || undefined }))}
              className="w-36"
            />
          </CardBody>
        </Card>

        {/* Tasks Table */}
        <Card>
          {isLoading ? (
            <CardBody>
              <p className="text-gray-500 dark:text-gray-400">Loading tasks...</p>
            </CardBody>
          ) : tasks.length === 0 ? (
            <EmptyState
              icon={CheckSquare}
              title="No tasks found"
              description="Create a new task to get started"
              action={{
                label: 'Create Task',
                onClick: () => setIsCreateOpen(true),
              }}
            />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Task</TableHeader>
                  <TableHeader>Project</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Priority</TableHeader>
                  <TableHeader>Time Logged</TableHeader>
                  <TableHeader>Assignees</TableHeader>
                  <TableHeader>Due Date</TableHeader>
                  <TableHeader className="w-12">{''}</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((task: any) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    onEdit={setEditingTask}
                    onDelete={setDeletingTask}
                    canDelete={isManager}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </PageContainer>

      {/* Create Modal */}
      <TaskFormModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} task={null} />

      {/* Edit Modal */}
      <TaskFormModal
        isOpen={!!editingTask}
        onClose={() => setEditingTask(null)}
        task={editingTask}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deletingTask}
        onClose={() => setDeletingTask(null)}
        onConfirm={handleDelete}
        title="Delete Task"
        message={`Are you sure you want to permanently delete "${deletingTask?.title}"? This will also delete all subtasks, time entries, and comments. This action cannot be undone.`}
        confirmLabel="Delete Permanently"
        isLoading={deleteTask.isPending}
      />
    </>
  );
}

function TaskRow({
  task,
  onEdit,
  onDelete,
  canDelete,
}: {
  task: any;
  onEdit: (task: Task) => void;
  onDelete: (task: Task) => void;
  canDelete: boolean;
}) {
  const updateTask = useUpdateTask();

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updateTask.mutateAsync({
        id: task.id,
        data: { status: newStatus as any },
      });
    } catch (error) {
      console.error('Failed to update task status:', error);
    }
  };

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <Badge variant="gray" className="text-xs">
            {task.type}
          </Badge>
          <button
            onClick={() => onEdit(task)}
            className="font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 text-left"
          >
            {task.title}
          </button>
        </div>
        {task._count?.subtasks > 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{task._count.subtasks} subtask(s)</p>
        )}
      </TableCell>
      <TableCell>
        <Link
          to="/project/$projectId"
          params={{ projectId: task.project?.id }}
          className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
        >
          {task.project?.name}
        </Link>
      </TableCell>
      <TableCell>
        <select
          value={task.status}
          onChange={(e) => handleStatusChange(e.target.value)}
          className="text-xs border-0 bg-transparent cursor-pointer focus:ring-0 p-0"
          disabled={updateTask.isPending}
        >
          <option value="backlog">Backlog</option>
          <option value="todo">To Do</option>
          <option value="in_progress">In Progress</option>
          <option value="review">In Review</option>
          <option value="done">Done</option>
        </select>
      </TableCell>
      <TableCell>
        <PriorityBadge priority={task.priority} />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1 text-sm">
          <Clock className="h-3.5 w-3.5 text-gray-400" />
          <span className={task.timeLoggedMinutes > 0 ? 'font-medium' : 'text-gray-400'}>
            {task.timeLoggedMinutes > 0 ? formatDuration(task.timeLoggedMinutes) : '-'}
          </span>
          {task.estimatedHours && <span className="text-gray-400">/ {task.estimatedHours}h</span>}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex -space-x-2">
          {task.assignments?.slice(0, 3).map((a: any) => (
            <Avatar
              key={a.employee.id}
              src={a.employee.avatarUrl}
              name={`${a.employee.firstName} ${a.employee.lastName}`}
              size="sm"
              className="border-2 border-white dark:border-gray-900"
            />
          ))}
          {task.assignments?.length > 3 && (
            <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium border-2 border-white dark:border-gray-900">
              +{task.assignments.length - 3}
            </div>
          )}
          {(!task.assignments || task.assignments.length === 0) && (
            <span className="text-gray-400 text-sm">Unassigned</span>
          )}
        </div>
      </TableCell>
      <TableCell>
        {task.dueDate ? (
          <span
            className={
              new Date(task.dueDate) < new Date() && task.status !== 'done' ? 'text-red-600 dark:text-red-400' : ''
            }
          >
            {formatDate(new Date(task.dueDate))}
          </span>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell>
        <Dropdown
          trigger={
            <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
              <MoreHorizontal className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </button>
          }
          align="right"
          width="w-36"
        >
          <DropdownItem onClick={() => onEdit(task)}>
            <div className="flex items-center gap-2">
              <Pencil className="h-4 w-4" />
              Edit
            </div>
          </DropdownItem>
          {task.status !== 'cancelled' && (
            <DropdownItem
              onClick={() => {
                updateTask.mutate({ id: task.id, data: { status: 'cancelled' } });
              }}
              className="text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950"
            >
              <div className="flex items-center gap-2">
                <Ban className="h-4 w-4" />
                Cancel
              </div>
            </DropdownItem>
          )}
          {canDelete && (
            <DropdownItem onClick={() => onDelete(task)} className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950">
              <div className="flex items-center gap-2">
                <Trash2 className="h-4 w-4" />
                Delete
              </div>
            </DropdownItem>
          )}
        </Dropdown>
      </TableCell>
    </TableRow>
  );
}
